import { ReactNode, forwardRef, memo } from 'react';
import {
  Box,
  Divider,
  makeStyles,
  MenuItem,
  PopoverOrigin,
  Theme,
  Tooltip,
  Typography,
  useTheme,
  withStyles
} from '@material-ui/core';
import clsx from 'clsx';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { useColors, EColors } from '../../hooks';
import { SDropdownMenu } from '../index';
import { TextColor } from '../../enums';

const DropdownText = withStyles({
  root: {
    fontSize: '0.9rem',
    color: 'inherit'
  }
})(Typography);

const DropdownSubtext = withStyles({
  root: {
    fontSize: '0.75rem',
    color: '#92929D',
    whiteSpace: 'normal'
  }
})(Typography);

const useMenuItemStyles = makeStyles<Theme, { color: string }>(
  ({ spacing, palette }) => ({
    // Menu tooltips keep the line breaks their text was written with, so an
    // explanation that needs two sentences can put them on two lines. Text
    // without a break is unaffected — it still wraps at the tooltip's width.
    tooltip: {
      whiteSpace: 'pre-line'
    },
    itemContainer: {
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gridGap: '4px',
      padding: spacing(1),
      color: ({ color }) => color || '#212121'
    },
    labelWrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    borderedBox: {
      border: '1px solid #F1F1F5',
      borderRadius: 6,
      minWidth: 135,
      height: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    innerBox: {
      padding: spacing(0, 2)
    },
    // Positioned against the MenuItem (MUI makes it relative) with no `left`, so
    // the rule starts at its static position: exactly where the label text does.
    textDivider: {
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        bottom: 0,
        height: 1,
        background: palette.divider
      }
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fill: ({ color }: { color: string }) => color || TextColor.Grey,
      color: ({ color }) => color || TextColor.Grey,
      margin: 0
    },
    bold: {
      fontWeight: 600
    }
  })
);

export interface ISMenuItem {
  icon?: ReactNode;
  // Usually a plain label; a node allows placeholder rows (e.g. a Skeleton
  // while an item's availability is still being resolved).
  text: ReactNode;
  divider?: boolean;
  /** 'text' runs the divider from the label to the menu's edge */
  dividerAlign?: 'text';
  subText?: string;
  /** Optional trailing adornment (e.g. a "New" badge) shown at the row end. */
  badge?: ReactNode;
  color?: string;
  disabled?: boolean;
  tooltipText?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  boldText?: boolean;
  borderedBox?: boolean;
  closeOnClick?: boolean;
  onClick?: () => void;
  handleClose?: () => void;
  submenu?: ISMenuItem[];
  // ! This will be removed once the new design is implemented
  newDesign?: boolean;
}

interface SMenuItemProps extends ISMenuItem {
  submenuAnchorOrigin?: PopoverOrigin;
  submenuTransformOrigin?: PopoverOrigin;
}

const SMenuItem = forwardRef(
  (
    {
      icon,
      text,
      subText,
      badge,
      color,
      divider,
      dividerAlign,
      disabled,
      tooltipText,
      tooltipPlacement = 'top',
      boldText,
      borderedBox,
      closeOnClick = true,
      onClick,
      handleClose,
      submenu,
      newDesign,
      submenuAnchorOrigin,
      submenuTransformOrigin
    }: SMenuItemProps,
    ref
  ) => {
    const { spacing } = useTheme();
    const { newColor } = useColors({ color: color as EColors });

    const classes = useMenuItemStyles({ color: newColor });
    const hasSubmenu = submenu?.length > 0;

    const handleClick = async () => {
      if (disabled) return;

      try {
        const result: unknown = onClick?.();
        // Keep the menu open until an async action settles (so a loading state
        // can show on the item), then close.
        if (result instanceof Promise) await result;
      } finally {
        // Don't close menu when clicking on items with submenus - the submenu should open instead
        if (closeOnClick && !hasSubmenu) handleClose?.();
      }
    };

    const menuItem = (
      <MenuItem
        component="div"
        disabled={disabled}
        style={{
          width: '100%',
          padding: 0,
          display: 'flex',
          alignItems: 'start',
          flexDirection: 'column'
        }}
        onClick={() => handleClick()}>
        <Box
          style={{
            width: '100%',
            padding: spacing(2, 3),
            boxSizing: 'border-box'
          }}>
          <Box className={classes.itemContainer}>
            {icon && <Box className={classes.icon}>{icon}</Box>}

            <Box
              className={clsx(classes.labelWrapper, {
                [classes.borderedBox]: borderedBox
              })}>
              <Box
                className={clsx(classes.innerBox, {
                  [classes.textDivider]: divider && dividerAlign === 'text'
                })}>
                <DropdownText
                  className={clsx({
                    [classes.bold]: boldText
                  })}>
                  {text}
                </DropdownText>
                {subText && <DropdownSubtext>{subText}</DropdownSubtext>}
              </Box>

              {badge}
              {hasSubmenu && <ChevronRightIcon />}
            </Box>
          </Box>
        </Box>

        {divider && dividerAlign !== 'text' && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'end',
              flexDirection: 'column',
              paddingRight: 15
            }}>
            <Divider style={{ width: '65%' }} />
          </Box>
        )}
      </MenuItem>
    );

    return (
      <Tooltip
        ref={ref}
        title={tooltipText || ''}
        arrow
        classes={{ tooltip: classes.tooltip }}
        disableHoverListener={!tooltipText}
        placement={tooltipPlacement}>
        <li>
          {hasSubmenu && !disabled ? (
            <SDropdownMenu
              newDesign={newDesign}
              hasSubmenu={hasSubmenu}
              anchorOrigin={
                submenuAnchorOrigin || {
                  vertical: 'bottom',
                  horizontal: 'right'
                }
              }
              transformOrigin={
                submenuTransformOrigin || {
                  vertical: 'top',
                  horizontal: 'right'
                }
              }
              onClose={handleClose}
              menuItems={submenu}>
              {menuItem}
            </SDropdownMenu>
          ) : (
            menuItem
          )}
        </li>
      </Tooltip>
    );
  }
);

SMenuItem.displayName = 'SMenuItem';

export default memo(SMenuItem);
